import axios from "axios";
import { toast } from "sonner";
import { getRuntimeApiBase } from "@/utils/runtimeApi";

const API_BASE = getRuntimeApiBase();

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  withCredentials: true,  // Send httpOnly cookies with every request
});

// Endpoints that must NOT receive a persisted Authorization header.
// Bootstrap auth uses short-lived tempToken values; an old localStorage token after redeploy
// must never overwrite these temporary tokens.
const NO_PERSISTED_AUTH_PATHS = [
  "/auth/patient/",
  "/auth/login",
  "/auth/verify-security-code",
  "/auth/setup-security-code",
  "/auth/first-login-change-password",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/health",
];

// Request interceptor: attach token only for authenticated endpoints
api.interceptors.request.use((config) => {
  const isNoPersistedAuth = NO_PERSISTED_AUTH_PATHS.some(p => config.url?.includes(p));
  const hasExplicitAuthorization = Boolean(config.headers?.Authorization || config.headers?.authorization);
  if (!isNoPersistedAuth && !hasExplicitAuthorization && !config.skipAuth) {
    const token = localStorage.getItem("hana_access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Response interceptor: handle token refresh + global error feedback
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't refresh/redirect inside bootstrap auth endpoints — they manage tempToken errors locally.
    const isNoPersistedAuthEndpoint = NO_PERSISTED_AUTH_PATHS.some(p => originalRequest?.url?.includes(p));
    if (isNoPersistedAuthEndpoint || originalRequest?.skipAuth) {
      return Promise.reject(error);
    }

    // Some patient-side helper requests are optional fallbacks (audio prompts, care config).
    // They may safely fail without invalidating the whole authenticated screen.
    if (error.response?.status === 401 && originalRequest?.skipAuthRedirect) {
      return Promise.reject(error);
    }

    // --- Global error feedback (timeout, network, 503) ---
    if (!error.response) {
      // Network error or timeout
      if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        toast.error("Connexion lente, veuillez reessayer.", { id: "global-timeout" });
      } else {
        toast.error("Pas de connexion internet. Verifiez votre reseau.", { id: "global-network" });
      }
      return Promise.reject(error);
    }

    if (error.response.status === 503) {
      toast.error("Service temporairement indisponible. Reessayez dans quelques instants.", { id: "global-503" });
      return Promise.reject(error);
    }

    // --- Token refresh on 401 ---
    if (error.response?.status === 401 && !originalRequest._retry) {
      // LOT 5 : header backend signale que la signature est invalide (rotation secret, token forgé…)
      // Dans ce cas, pas de retry, pas de refresh : logout immédiat.
      const versionMismatch = error.response.headers?.["x-token-version-mismatch"];
      if (versionMismatch === "1") {
        localStorage.removeItem("hana_user");
        localStorage.removeItem("hana_access_token");
        localStorage.removeItem("hana_refresh_token");
        localStorage.removeItem("patientSessionToken");
        sessionStorage.removeItem("hana_otp_pending");
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      
      try {
        // Refresh via cookie (httpOnly) — also try body for backward compat
        const refreshToken = localStorage.getItem("hana_refresh_token");
        const res = await axios.post(`${API_BASE}/auth/refresh`, 
          refreshToken ? { refreshToken } : {},
          { withCredentials: true }
        );
        const { accessToken, refreshToken: rotatedRefresh } = res.data;
        localStorage.setItem("hana_access_token", accessToken);
        if (rotatedRefresh) localStorage.setItem("hana_refresh_token", rotatedRefresh);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Réseau coupé / serveur injoignable : ne pas déconnecter, l'appel sera rejoué plus tard
        if (!refreshError?.response) {
          return Promise.reject(error);
        }
        localStorage.removeItem("hana_user");
        localStorage.removeItem("hana_access_token");
        localStorage.removeItem("hana_refresh_token");
        localStorage.removeItem("patientSessionToken");
        sessionStorage.removeItem("hana_otp_pending");
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

export default api;
